import { useState } from 'react';
import clsx from 'clsx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AgentDeviceView, DeviceAgentView } from '@1qa/shared';
import { CheckCircle2, ChevronDown, Download, Info, MonitorSmartphone, Pause, Play, Plus, Smartphone, Trash2 } from '@/components/icons';
import { CopyField } from '@/components/CopyField';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatDateTime, timeAgo } from '@/lib/format';
import { DEVICE_AGENT_DOWNLOAD, agentVersionName, deviceAgentFileName, fetchLatestDeviceAgent, type DeviceAgentBuild } from '@/lib/deviceAgentBuild';
import { versionAtLeast } from '@/lib/recorderBridge';
import { Badge, Button, Card, CardBody, Dialog, EmptyState, ErrorNotice, Input, PageHeader, PageLoader, buttonClass, useConfirm, useToast, type Tone } from '@/components/ui';

const DEVICE_STATUS: Record<AgentDeviceView['status'], { label: string; tone: Tone }> = {
  ready: { label: 'Ready', tone: 'success' },
  busy: { label: 'Busy', tone: 'pending' },
  unauthorized: { label: 'Allow USB debugging on the phone', tone: 'warning' },
  offline: { label: 'Offline', tone: 'muted' },
};

/**
 * Devices: phones on testers' computers, connected through the 1QA Device Agent (docs/design/device-agent.md). Anyone
 * who runs tests sees them (and picks them next to Run); Project Admins and Members connect their own.
 */
export function DevicesPage() {
  const { currentOrgId, can, user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [connecting, setConnecting] = useState(false);
  const agents = useQuery({
    queryKey: ['device-agents', currentOrgId],
    queryFn: () => api.deviceAgents(currentOrgId!),
    enabled: !!currentOrgId,
    refetchInterval: connecting ? 3000 : 10_000,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['device-agents', currentOrgId] });
  const update = useMutation({
    mutationFn: ({ a, paused }: { a: DeviceAgentView; paused: boolean }) => api.updateDeviceAgent(a.id, { paused }),
    onSuccess: (a) => {
      toast.success(a.paused ? `${a.name} is paused: no tests run there` : `${a.name} takes tests again`);
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (a: DeviceAgentView) => api.deleteDeviceAgent(a.id),
    onSuccess: () => {
      toast.success('Device agent removed');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const canConnect = can('device.connect');
  const latest = useQuery({
    queryKey: ['device-agent-latest'],
    queryFn: fetchLatestDeviceAgent,
    staleTime: 60_000,
  });
  const outdated = (a: DeviceAgentView) => !!(latest.data && a.agentVersion && a.agentVersion !== 'dev' && !versionAtLeast(a.agentVersion, latest.data.version));

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Mobile"
        title="Devices"
        description="Run Android tests on real phones plugged into a tester’s computer. The 1QA Device Agent on that computer connects them; tests are picked and run from 1QA as usual."
        actions={
          <>
            <a href={DEVICE_AGENT_DOWNLOAD} download={deviceAgentFileName(latest.data ?? null)} className={buttonClass('outline', 'md')}>
              <Download className="size-3" /> Download agent
              {latest.data ? ` ${latest.data.versionName}` : ''}
            </a>
            {canConnect && (
              <Button onClick={() => setConnecting(true)} icon={<Plus className="size-4" />}>
                Connect a device
              </Button>
            )}
          </>
        }
      />
      {/* Waits for the agent list, so it opens by itself only for an organization without agents. */}
      {agents.isSuccess && <BeforeYouStart build={latest.data ?? null} defaultOpen={agents.data.length === 0} />}
      {connecting && currentOrgId && <ConnectDialog orgId={currentOrgId} agents={agents.data ?? []} build={latest.data ?? null} onClose={() => setConnecting(false)} />}
      {agents.isPending ? (
        <PageLoader />
      ) : agents.isError ? (
        <ErrorNotice error={agents.error} onRetry={() => agents.refetch()} />
      ) : agents.data.length === 0 ? (
        <EmptyState
          icon={<MonitorSmartphone className="size-6" />}
          title="No phones connected yet"
          description="Plug a phone into your computer, start the 1QA Device Agent there, and the phone shows up here and in the Device picker next to Run. It takes about five minutes the first time."
          action={
            canConnect && (
              <Button onClick={() => setConnecting(true)} icon={<Plus className="size-4" />}>
                Connect a device
              </Button>
            )
          }
        />
      ) : (
        <div className="space-y-4">
          {agents.data.map((a) => (
            <Card key={a.id} className="overflow-hidden" data-testid="device-agent">
              <div className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-4">
                <span className="grid size-9 place-items-center rounded-card bg-primary-subtle text-primary">
                  <MonitorSmartphone className="size-[18px]" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-fg">
                    {a.name}
                    {a.paused ? <Badge tone="warning">Paused</Badge> : a.online ? <Badge tone="success">Online</Badge> : <Badge tone="muted">Offline</Badge>}
                    {outdated(a) && <Badge tone="info">Update available</Badge>}
                  </div>
                  <div className="truncate text-xs text-fg-secondary">
                    {a.hostname ? `${a.hostname} · ` : ''}
                    {a.online ? 'connected now' : a.lastSeenAt ? `last seen ${timeAgo(a.lastSeenAt)}` : 'never connected'}
                    {a.agentVersion && ` · agent ${agentVersionName(a.agentVersion)}`} · by {a.createdBy.id === user?.id ? 'you' : a.createdBy.name}
                  </div>
                </div>
                {a.canManage && (
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      loading={update.isPending && update.variables?.a.id === a.id}
                      onClick={() => update.mutate({ a, paused: !a.paused })}
                      icon={a.paused ? <Play className="size-4" /> : <Pause className="size-4" />}
                    >
                      {a.paused ? 'Resume' : 'Pause'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Remove ${a.name}`}
                      loading={remove.isPending && remove.variables?.id === a.id}
                      onClick={async () => {
                        const ok = await confirm({
                          title: `Remove ${a.name}?`,
                          message: 'Its token stops working at once and its phones disappear from 1QA. You can connect the computer again later with a new token.',
                          confirmLabel: 'Remove',
                        });
                        if (ok) remove.mutate(a);
                      }}
                      icon={<Trash2 className="size-4" />}
                    />
                  </div>
                )}
              </div>
              {outdated(a) && latest.data && (
                <p className="flex items-start gap-2 border-b border-border-subtle px-5 py-3 text-xs text-fg-secondary">
                  <Info className="mt-0.5 size-3.5 shrink-0 text-info" aria-hidden />
                  Version {latest.data.versionName} is available. Download it, unzip it over the old folder, run npm install there, and start the agent again with the same command.
                </p>
              )}
              {!a.online ? (
                <p className="px-5 py-4 text-sm text-fg-secondary">
                  The agent is not running. On {a.hostname ?? 'that computer'}, start it again with the command from when it was connected.
                </p>
              ) : a.devices.length === 0 ? (
                <p className="px-5 py-4 text-sm text-fg-secondary">No phone connected. Plug one in with USB debugging on.</p>
              ) : (
                <ul className="divide-y divide-border-subtle">
                  {a.devices.map((d) => (
                    <li key={d.serial} className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid="agent-device">
                      <Smartphone className="size-4 text-fg-muted" aria-hidden />
                      <span className="text-sm font-medium text-fg">{d.label}</span>
                      <span className="font-mono text-xs text-fg-muted">{d.serial}</span>
                      <span className="ml-auto">
                        <Badge tone={DEVICE_STATUS[d.status].tone}>{a.paused && d.status === 'busy' ? 'Paused' : DEVICE_STATUS[d.status].label}</Badge>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/** Name the agent, then the one-time setup: download, install, run with the token, plug in the phone. */
function ConnectDialog({ orgId, agents, build, onClose }: { orgId: string; agents: DeviceAgentView[]; build: DeviceAgentBuild | null; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [created, setCreated] = useState<{ agent: DeviceAgentView; token: string } | null>(null);
  const create = useMutation({
    mutationFn: () => api.createDeviceAgent(orgId, name.trim()),
    onSuccess: (res) => {
      setCreated(res);
      qc.invalidateQueries({ queryKey: ['device-agents', orgId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const live = created && agents.find((a) => a.id === created.agent.id);
  const server = window.location.origin;
  const command = created ? `node 1qa-device-agent.mjs --server ${server} --token ${created.token}` : '';

  if (!created) {
    return (
      <Dialog
        open
        onClose={onClose}
        title="Connect a device"
        description="Give the computer the phones are plugged into a name, so everyone knows whose phones they are."
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => create.mutate()} disabled={!name.trim()} loading={create.isPending}>
              Continue
            </Button>
          </>
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Dana’s laptop, QA lab Mac" maxLength={60} aria-label="Name" autoFocus />
        </form>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} size="lg" title={`Set up ${created.agent.name}`} description="Do this on the computer the phones are plugged into. It needs Node.js 22 and Android platform-tools (adb).">
      <ol className="ml-5 list-decimal space-y-4 text-sm text-fg-secondary marker:font-semibold marker:text-primary">
        <li>
          <a href={DEVICE_AGENT_DOWNLOAD} download={deviceAgentFileName(build)} className="inline-flex items-center gap-1.5 font-medium text-link hover:underline">
            <Download className="size-4" /> Download the 1QA Device Agent
            {build ? ` ${build.versionName}` : ''}
          </a>{' '}
          and unzip it. In the <code>1qa-device-agent</code> folder, run <code className="rounded-bar bg-surface-raised px-1.5 py-0.5">npm install</code> once.
        </li>
        <li>
          <span>Start it there with this command (it contains the agent’s token: keep it to yourself):</span>
          <div className="mt-2">
            <CopyField label="Command" value={command} secret />
          </div>
          <p className="mt-1 text-xs text-fg-muted">The token is shown only now. Lost it? Remove this agent and connect the computer again.</p>
        </li>
        <li>
          On the phone, turn on <strong>Developer options</strong> (tap <em>Build number</em> 7 times in Settings › About phone), then <strong>USB debugging</strong>. Plug
          it in and tap <strong>Allow</strong> when it asks about this computer.
        </li>
      </ol>
      <div className="mt-5 rounded-card bg-surface-sunken p-4 text-sm ring-1 ring-border ring-inset" role="status">
        {!live?.online ? (
          <span className="text-fg-secondary">Waiting for the agent to connect…</span>
        ) : live.devices.length === 0 ? (
          <span className="flex items-center gap-2 text-fg">
            <CheckCircle2 className="size-4 text-success" /> {live.name} is connected. Now plug in a phone.
          </span>
        ) : (
          <span className="flex items-center gap-2 text-fg">
            <CheckCircle2 className="size-4 text-success" /> Connected: {live.devices.map((d) => d.label).join(', ')}. Choose it in the Device picker next to Run.
          </span>
        )}
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={onClose}>Done</Button>
      </div>
    </Dialog>
  );
}

const PREREQUISITES: {
  title: string;
  items: { text: string; check?: string }[];
}[] = [
  {
    title: 'On the computer',
    items: [
      { text: 'Windows, macOS or Linux, kept awake while tests run' },
      { text: 'Node.js 22 or newer', check: 'node --version' },
      {
        text: 'Android platform-tools (adb) on PATH, or in the Android SDK (ANDROID_HOME)',
        check: 'adb devices',
      },
      {
        text: 'Internet access to this 1QA address (the agent only calls out; nothing connects to the computer)',
      },
    ],
  },
  {
    title: 'On the phone',
    items: [
      {
        text: 'An Android phone and a USB cable that carries data (some only charge)',
      },
      {
        text: 'Developer options: Settings › About phone › tap Build number 7 times',
      },
      { text: 'USB debugging on (Settings › System › Developer options)' },
      {
        text: 'Tap Allow when the phone asks about the computer, with “Always allow from this computer” ticked',
      },
    ],
  },
];

/** What a computer and phone need, and the agent download with its latest build. */
/** Collapsed unless asked for; open at first, while the organization has no agent yet. */
function BeforeYouStart({ build, defaultOpen }: { build: DeviceAgentBuild | null; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card>
      <button
        type="button"
        aria-expanded={open}
        aria-controls="before-you-start"
        onClick={() => setOpen((v) => !v)}
        className={clsx('flex w-full items-start gap-3 px-5 py-4 text-left', open && 'border-b border-border-subtle')}
      >
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-fg">Before you start</h3>
          <p className="mt-0.5 text-sm text-fg-secondary">
            What the computer and the phone need, and how to set up the agent (once per computer)
            {build ? ` · latest agent ${build.versionName}` : ''}.
          </p>
        </div>
        <ChevronDown className={clsx('mt-1 size-4 shrink-0 text-fg-muted transition-transform', open && 'rotate-180')} aria-hidden />
      </button>
      {open && (
        <CardBody id="before-you-start" className="grid gap-6 md:grid-cols-3">
          {PREREQUISITES.map((group) => (
            <div key={group.title}>
              <h3 className="mb-2 text-xs font-semibold tracking-label text-fg-muted uppercase">{group.title}</h3>
              <ul className="space-y-2 text-sm text-fg-secondary">
                {group.items.map((item) => (
                  <li key={item.text} className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-primary" aria-hidden />
                    <span>
                      {item.text}
                      {item.check && (
                        <>
                          {' '}
                          <span className="text-fg-muted">check:</span> <code className="rounded-bar bg-surface-raised px-1.5 py-0.5 font-mono text-xs text-fg">{item.check}</code>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div data-testid="device-agent-download">
            <h3 className="mb-2 text-xs font-semibold tracking-label text-fg-muted uppercase">The agent</h3>
            <p className="text-xs text-fg-muted">
              {build ? (
                <>
                  Latest build: version {build.versionName}, built {formatDateTime(build.builtAt)}
                  {build.commit ? ` from ${build.commit}` : ''}.
                </>
              ) : (
                'A zip with the agent script (1qa-device-agent.mjs), its package.json and a README.'
              )}
            </p>
            <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-fg-secondary">
              <li>
                Press <strong>Download agent</strong> at the top of this page and unzip it into a folder you will keep.
              </li>
              <li>
                In the <code className="rounded-bar bg-surface-raised px-1.5 py-0.5 font-mono text-xs text-fg">1qa-device-agent</code> folder, run{' '}
                <code className="rounded-bar bg-surface-raised px-1.5 py-0.5 font-mono text-xs text-fg">npm install</code> once.
              </li>
              <li>
                Press <strong>Connect a device</strong> for the start command with this computer’s token.
              </li>
            </ol>
          </div>
        </CardBody>
      )}
    </Card>
  );
}
